<template>
    <form>
        <div class="bookings">
            <span class="title">Booking(s):</span>

            <div
                v-for="(book, index) in bookings"
                :key="book.uniqueId2"
                class="item"
            >
                <div class="details">
                    <span class="room">{{ book.roomName }}</span>
                    <br />
                    <span class="dates">
                        {{ displayBookingDate(book.start, book.end) }}
                    </span>
                </div>
                <a
                    v-if="directSplitMode ? true : index > 0"
                    href="#"
                    class="cancel"
                    @click.prevent="cancelBooking(book)"
                >
                    Cancel
                </a>
            </div>
        </div>

        <b-form-group label-cols="3" label="Check In:" label-for="dateCheckIn">
            <date-picker
                name="dateCheckIn"
                :config="datePickerOptions"
                v-model="state.dateCheckIn"
                placeholder="Check-in Date (required)"
                :disabled="isCheckingAvailability"
                @dp-change="value => handleDpChange(value, 'in')"
            />
        </b-form-group>
        <b-form-group
            label-cols="3"
            label="Check Out:"
            label-for="dateCheckOut"
        >
            <date-picker
                name="dateCheckOut"
                :config="datePickerOptionsCheckOut"
                v-model="state.dateCheckOut"
                placeholder="Check-out Date (required)"
                :disabled="isCheckingAvailability"
                @dp-change="value => handleDpChange(value, 'out')"
            />
        </b-form-group>
        <b-form-group label-cols="3" label="Room:" label-for="room">
            <v-select
                id="room"
                name="room"
                :options="roomList"
                v-model="state.selectedRoom"
                placeholder="Room (required)"
                item-text="label"
                :disabled="isCheckingAvailability"
            />
        </b-form-group>
        <b-form-group
            label-cols="3"
            label=" "
            v-if="state.dateCheckIn && state.dateCheckOut && state.selectedRoom"
        >
            <span class="note" v-if="isCheckingAvailability">
                Checking availability...
            </span>
            <span class="note" v-else>Click "Split" button to apply</span>
        </b-form-group>
    </form>
</template>

<script>
import vSelect from "vue-select";
import Moment from "moment";
import { extendMoment } from "moment-range";
import BoardingService from "@/services/boarding.js";

const moment = extendMoment(Moment);

export default {
    props: {
        directSplitMode: {
            type: Boolean,
            default: false
        }
    },
    components: {
        vSelect
    },
    computed: {
        roomList() {
            return this.$store.state.modalReservation.roomList;
        },
        bookings: {
            get() {
                return this.$store.state.modalReservation.bookings;
            },
            set(value = []) {
                this.$store.commit("modalReservation/setBookings", value);
            }
        },
        state: {
            get() {
                return this.$store.state.modalReservation.view.SPLIT_RESERVATION; //prettier-ignore
            },
            set(value) {
                this.$store.commit("modalReservation/setViewState", {
                    view: "SPLIT_RESERVATION",
                    value: value
                });
            }
        },
        datePickerOptionsCheckOut() {
            if (this.state.dateCheckIn) {
                return {
                    ...this.datePickerOptions,
                    minDate: this.state.dateCheckIn
                };
            }

            return this.datePickerOptions;
        },
        checkInTime() {
            return this.$store.state.modalReservation.checkInTime;
        },
        checkOutTime() {
            return this.$store.state.modalReservation.checkOutTime;
        }
    },
    data() {
        return {
            datePickerOptions: {},
            isCheckingAvailability: false
        };
    },
    async mounted() {
        await this._getSetting();

        this.datePickerOptions = {
            format: this.getDateFormat(),
            timeZone: this._timeZone
        };

        this.resetForm(); // always start with nothing
    },
    methods: {
        handleDpChange(value, type) {
            if (!value.date) {
                return;
            }

            let mapping = {
                in: "dateCheckIn",
                out: "dateCheckOut"
            };

            this.state[mapping[type]] = value.date.toDate();
        },
        displayBookingDate(start, end) {
            let format = this.getDateFormat();
            let s = moment.tz(start, this._timeZone).format(format);
            let e = moment.tz(end, this._timeZone).format(format);
            return `${s} - ${e}`;
        },
        resetForm() {
            this.$store.commit("modalReservation/clearSplitForm");

            // preset check in/out value
            let latest = this.bookings[this.bookings.length - 1];

            if (latest) {
                let format1 = "YYYY-MM-DD";
                let format2 = "YYYY-MM-DD HH:mm";

                let lastDayStr = moment.tz(latest.end, this._timeZone).format(format1) + " " + this.checkInTime; // prettier-ignore
                let nextInMoment = moment.tz(lastDayStr, format2, this._timeZone); // prettier-ignore

                let nextOutStr = nextInMoment.clone().add(1, "day").format(format1) + " " + this.checkOutTime; // prettier-ignore
                let nextOutMoment = moment.tz(nextOutStr, format2, this._timeZone); // prettier-ignore

                this.state.dateCheckIn = nextInMoment.toDate();
                this.state.dateCheckOut = nextOutMoment.toDate();
            }
        },
        /**
         * Do not remove this method!
         * It is called/used from outside of this file via refs.
         */
        async registerBooking() {
            function fixTime(momentObj, time, tz) {
                let f1 = "YYYYMMDD";
                let f2 = "YYYYMMDDHHmm";
                let str = momentObj.format(f1);
                str += time;
                return moment.tz(str, f2, tz);
            }

            try {
                let { dateCheckIn, dateCheckOut, selectedRoom } = this.state;
                let startMoment = moment.tz(dateCheckIn, this._timeZone);
                let endMoment = moment.tz(dateCheckOut, this._timeZone);

                // compare time
                if (startMoment.format("HHmm") !== this.checkInTime) {
                    startMoment = fixTime(startMoment, this.checkInTime, this._timeZone); // prettier-ignore
                }

                if (endMoment.format("HHmm") !== this.checkOutTime) {
                    endMoment = fixTime(endMoment, this.checkOutTime, this._timeZone); // prettier-ignore
                }

                let startDate = startMoment.toDate();
                let endDate = endMoment.toDate();
                let messageNA = "Unable to add. The room is not available at the selected dates."; // prettier-ignore
                this.isCheckingAvailability = true;

                // check locally
                let isLocallyValid = this.checkWithBookings(selectedRoom.id, startDate, endDate); // prettier-ignore
                if (!isLocallyValid) {
                    throw new Error(messageNA);
                }

                // then check with db
                let isAvailable = await BoardingService.checkRoomAvailability(
                    startMoment.unix(),
                    endMoment.unix(),
                    selectedRoom.id
                );
                if (!isAvailable) {
                    throw new Error(messageNA);
                }

                this.$store.dispatch("modalReservation/registerBooking", {
                    roomId: selectedRoom.id,
                    roomName: selectedRoom.label,
                    start: startMoment.toDate(),
                    end: endMoment.toDate(),
                    timezone: this._timeZone
                });

                this.resetForm();
                this.isCheckingAvailability = false;
            } catch (err) {
                this._message(err.message, "danger");
                this.isCheckingAvailability = false;
            }
        },
        cancelBooking(booking) {
            let updatedList = this.bookings.filter(
                rec => rec.uniqueId2 !== booking.uniqueId2
            );

            this.bookings = updatedList;
        },
        checkWithBookings(roomId, start, end) {
            let d1 = moment(start).startOf("day");
            let d2 = moment(end).endOf("day");
            let range1 = moment.range([d1, d2]);

            for (let booking of this.bookings) {
                let d3 = moment(booking.start).startOf("day");
                let d4 = moment(booking.end).endOf("day");
                let range2 = moment.range([d3, d4]);

                if (range1.overlaps(range2) && booking.roomId === roomId) {
                    return false;
                }
            }

            return true;
        }
    }
};
</script>

<style lang="scss" scoped>
.bookings {
    border-bottom: 1px solid #dbdbdb;
    // padding-bottom: 15px;
    margin-bottom: 15px;

    .title {
        font-weight: bold;
        margin-bottom: 11px;
        display: block;
    }

    .item {
        display: flex;
        padding: 10px 0;
        border-top: 1px dotted #d5d5d5;
        align-items: center;

        &:hover {
            background: #fbfbfb;

            .cancel {
                font-weight: bold;
                display: unset;
            }
        }

        .details {
            flex: 1;

            .dates {
                color: #626262;
                font-size: 0.93em;
            }
        }

        .cancel {
            // font-weight: normal;
            display: none;
        }
    }
}
.note {
    margin: 0;
    font-size: 0.9em;
    color: #5d5d5d;
    font-style: italic;
}
</style>
